import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Modal from '@components/shared/Modal/Modal';
import Button from '@components/shared/Button/Button';
import Carousel from '@components/shared/Carousel/Carousel';
import styles from './Services.module.css';

const Services: React.FC = () => {
  const { config } = useConfig();
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!config || !config.components.services.enabled) return null;

  const { services } = config.components;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const handleServiceClick = (index: number) => {
    if (services.items[index].detailedDescription || services.items[index].gallery) {
      setSelectedService(index);
      setCurrentImageIndex(0); // Reset to first image when opening modal
    }
  };

  const closeModal = () => {
    setSelectedService(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedService !== null && services.items[selectedService].gallery) {
      const totalImages = services.items[selectedService].gallery!.images.length;
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const handlePrevImage = () => {
    if (selectedService !== null && services.items[selectedService].gallery) {
      const totalImages = services.items[selectedService].gallery!.images.length;
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <section id="services" className={styles.services}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Features Section - Display above service cards */}
          {services.items.length > 0 && services.items[0].features && services.items[0].features.length > 0 && (
            <motion.div className={styles.featuresSection} variants={itemVariants}>
              <div className={styles.featuresGrid}>
                {services.items[0].features.map((feature, idx) => (
                  <div key={idx} className={styles.featureCard}>
                    <i className={`fas fa-check-circle ${styles.featureIcon}`}></i>
                    <span className={styles.featureText}>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Services Grid */}
          <div className={styles.grid}>
            {services.items.map((service, index) => (
              <motion.div
                key={index}
                className={styles.serviceCard}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                onClick={() => handleServiceClick(index)}
              >
                {/* Service Image or Gallery */}
                {service.gallery?.enabled && service.gallery.images.length > 0 ? (
                  <div className={styles.serviceImageCarousel}>
                    <Carousel
                      autoplay
                      autoplaySpeed={4000}
                      dots
                      arrows={false}
                      fade={false}
                      className={styles.serviceCarousel}
                    >
                      {service.gallery.images.map((image, imgIndex) => (
                        <div key={imgIndex} className={styles.carouselSlide}>
                          <img
                            src={image}
                            alt={`${service.title} - Image ${imgIndex + 1}`}
                            className={styles.carouselImage}
                          />
                        </div>
                      ))}
                    </Carousel>
                  </div>
                ) : service.image ? (
                  <div className={styles.serviceImage}>
                    <img src={service.image} alt={service.title} />
                    <div className={styles.imageOverlay}>
                      <i className={`${service.icon} ${styles.overlayIcon}`}></i>
                    </div>
                  </div>
                ) : (
                  <div className={styles.serviceIcon}>
                    <i className={service.icon}></i>
                  </div>
                )}

                <div className={styles.serviceContent}>
                  <h3 className={styles.serviceTitle}>{service.title}</h3>
                  <p className={styles.serviceDescription}>{service.description}</p>

                  {(service.detailedDescription || service.gallery) && (
                    <button className={styles.learnMoreButton}>
                      Learn More
                      <i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem' }}></i>
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Service Detail Modal */}
      {selectedService !== null && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={services.items[selectedService].title}
          size="lg"
        >
          <div className={styles.modalContent}>
            {/* Manual Slideshow for Gallery or Single Image */}
            {services.items[selectedService].gallery?.enabled && 
             services.items[selectedService].gallery!.images.length > 0 ? (
              <div className={styles.manualSlideshow}>
                {/* Main Image Display */}
                <div className={styles.mainImageContainer}>
                  <img
                    src={services.items[selectedService].gallery!.images[currentImageIndex]}
                    alt={`${services.items[selectedService].title} - Image ${currentImageIndex + 1}`}
                    className={styles.mainImage}
                  />
                  
                  {/* Navigation Arrows - Only show if more than 1 image */}
                  {services.items[selectedService].gallery!.images.length > 1 && (
                    <>
                      <button
                        className={`${styles.navArrow} ${styles.navArrowLeft}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrevImage();
                        }}
                        aria-label="Previous image"
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                      <button
                        className={`${styles.navArrow} ${styles.navArrowRight}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNextImage();
                        }}
                        aria-label="Next image"
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>

                      {/* Image Counter */}
                      <div className={styles.imageCounter}>
                        {currentImageIndex + 1} / {services.items[selectedService].gallery!.images.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Thumbnail Navigation - Only show if more than 1 image */}
                {services.items[selectedService].gallery!.images.length > 1 && (
                  <div className={styles.thumbnailStrip}>
                    {services.items[selectedService].gallery!.images.map((image, imgIndex) => (
                      <div
                        key={imgIndex}
                        className={`${styles.thumbnail} ${imgIndex === currentImageIndex ? styles.thumbnailActive : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleThumbnailClick(imgIndex);
                        }}
                      >
                        <img src={image} alt={`Thumbnail ${imgIndex + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : services.items[selectedService].image ? (
              <img
                src={services.items[selectedService].image}
                alt={services.items[selectedService].title}
                className={styles.modalImage}
              />
            ) : null}

            <div className={styles.modalBody}>
              {services.items[selectedService].detailedDescription && (
                <p className={styles.detailedDescription}>
                  {services.items[selectedService].detailedDescription}
                </p>
              )}

              {services.items[selectedService].price && (
                <div className={styles.modalPrice}>
                  <strong>Price:</strong> {services.items[selectedService].price}
                </div>
              )}

              <div className={styles.modalActions}>
                <Button variant="primary" size="lg" onClick={() => window.location.href = 'tel:0425375837'}>
                  Call Us
                </Button>
                <Button variant="outline" size="lg" onClick={closeModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default Services;